import React, { useEffect, useState } from 'react';
import { ActivityFeedItem, AthleteProfile, FeedComment, SportType } from '../types';
import { saveActivityFeedItemToFirestore, subscribeActivityFeed } from '../services/firestoreService';
import { 
  Rss, 
  Flame, 
  Heart, 
  MessageCircle, 
  Share2, 
  PlusCircle, 
  Trophy, 
  Award, 
  Film, 
  MapPin, 
  Zap, 
  CheckCircle2, 
  Send, 
  UserPlus, 
  UserCheck, 
  Sparkles, 
  CalendarCheck, 
  BarChart3, 
  Users, 
  TrendingUp,
  Clock,
  ThumbsUp,
  Smile,
  Search,
  Filter,
  X,
  Crown,
  Swords,
  MessageSquare,
  SlidersHorizontal,
  Navigation,
  Target,
  Sliders
} from 'lucide-react';
import { PlayerCompareRadarChart } from './PlayerCompareRadarChart';

interface SocialFeedViewProps {
  user: AthleteProfile;
  allAthletes: AthleteProfile[];
  onNavigateTab: (tab: any) => void;
  onSendFriendRequest?: (player: { id: string; name: string; avatar?: string; detail?: string }) => void;
  onDirectMessageAthlete?: (athlete: AthleteProfile) => void;
}

export const SocialFeedView: React.FC<SocialFeedViewProps> = ({
  user,
  allAthletes,
  onNavigateTab,
  onSendFriendRequest,
  onDirectMessageAthlete,
}) => {
  const [filterType, setFilterType] = useState<string>('all'); // 'all' | 'game' | 'achievement' | 'highlight' | 'levelup'
  const [friendOnlyFilter, setFriendOnlyFilter] = useState<boolean>(false);
  const [newStatusText, setNewStatusText] = useState<string>('');
  const [activeCommentFeedId, setActiveCommentFeedId] = useState<string | null>(null);
  const [commentInputText, setCommentInputText] = useState<Record<string, string>>({});
  const [sentFriendRequests, setSentFriendRequests] = useState<string[]>([]);

  // Athlete Search and Advanced Filters
  const [athleteSearchQuery, setAthleteSearchQuery] = useState<string>('');
  const [athleteSportFilter, setAthleteSportFilter] = useState<string>('all');
  const [athleteSkillFilter, setAthleteSkillFilter] = useState<string>('all');
  const [proOnlyFilter, setProOnlyFilter] = useState<boolean>(false);
  const [nearbyOnlyFilter, setNearbyOnlyFilter] = useState<boolean>(false);
  const [similarSkillFilter, setSimilarSkillFilter] = useState<boolean>(false);
  const [sameSportFilter, setSameSportFilter] = useState<boolean>(false);
  const [maxDistanceKm, setMaxDistanceKm] = useState<string>('all'); // 'all' | '5' | '15' | '25' | '50'
  const [showAdvancedPanel, setShowAdvancedPanel] = useState<boolean>(false);

  // Distance calculation helper (Haversine formula in km)
  const getDistanceKm = (lat1?: number, lon1?: number, lat2?: number, lon2?: number) => {
    if (!lat1 || !lon1 || !lat2 || !lon2) return 3.2; // default simulated distance
    const R = 6371; // km
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return Math.round(R * c * 10) / 10;
  };

  // Player Radar Compare State
  const [showPlayerCompare, setShowPlayerCompare] = useState<boolean>(false);
  const [comparePlayer1, setComparePlayer1] = useState<AthleteProfile>(user);
  const [comparePlayer2, setComparePlayer2] = useState<AthleteProfile>(
    allAthletes.find((a) => a.id !== user.id) || user
  );

  // Filter allAthletes by query, sport, skill level, PRO status, distance, and user similarities
  const filteredAthletes = allAthletes.filter((ath) => {
    // Exclude users hidden from global search unless it's the user's own profile
    if (ath.id !== user.id && (ath.hideFromGlobalSearch || ath.isSearchable === false)) {
      return false;
    }

    const q = athleteSearchQuery.trim().toLowerCase();

    const matchesQuery =
      !q ||
      ath.name.toLowerCase().includes(q) ||
      ath.handle?.toLowerCase().includes(q) ||
      ath.primarySport.toLowerCase().includes(q) ||
      ath.levelTitle.toLowerCase().includes(q) ||
      `level ${ath.level}`.toLowerCase().includes(q) ||
      `lvl ${ath.level}`.toLowerCase().includes(q) ||
      (ath.position && ath.position.toLowerCase().includes(q)) ||
      (ath.schoolOrLeague && ath.schoolOrLeague.toLowerCase().includes(q));

    const matchesSport = athleteSportFilter === 'all' || ath.primarySport === athleteSportFilter;
    const matchesSkill = athleteSkillFilter === 'all' || ath.levelTitle === athleteSkillFilter;

    // PRO filter
    const isProMember = Boolean(ath.isPro || ath.subscriptionTier === 'pro');
    const matchesPro = !proOnlyFilter || isProMember;

    // Same primary sport
    const matchesSameSport = !sameSportFilter || ath.primarySport === user.primarySport;

    // Similar skill level (+/- 3 levels or same level title)
    const levelDiff = Math.abs(ath.level - user.level);
    const matchesSimilarSkill =
      !similarSkillFilter || levelDiff <= 3 || ath.levelTitle === user.levelTitle;

    // Nearby distance filter
    const dist = getDistanceKm(user.location?.lat, user.location?.lng, ath.location?.lat, ath.location?.lng);
    const numMaxDist = maxDistanceKm === 'all' ? Infinity : parseFloat(maxDistanceKm);
    const matchesNearby =
      (!nearbyOnlyFilter && maxDistanceKm === 'all') ||
      (nearbyOnlyFilter ? dist <= 25 : dist <= numMaxDist);

    return (
      matchesQuery &&
      matchesSport &&
      matchesSkill &&
      matchesPro &&
      matchesSameSport &&
      matchesSimilarSkill &&
      matchesNearby
    );
  });

  const activeAdvancedFilterCount =
    (proOnlyFilter ? 1 : 0) +
    (nearbyOnlyFilter || maxDistanceKm !== 'all' ? 1 : 0) +
    (similarSkillFilter ? 1 : 0) +
    (sameSportFilter ? 1 : 0) +
    (athleteSportFilter !== 'all' ? 1 : 0) +
    (athleteSkillFilter !== 'all' ? 1 : 0);

  const handleResetFilters = () => {
    setAthleteSearchQuery('');
    setAthleteSportFilter('all');
    setAthleteSkillFilter('all');
    setProOnlyFilter(false);
    setNearbyOnlyFilter(false);
    setSimilarSkillFilter(false);
    setSameSportFilter(false);
    setMaxDistanceKm('all');
  };

  // Activity feed is populated by real user actions and backend data.
  const [feedItems, setFeedItems] = useState<ActivityFeedItem[]>([]);

  useEffect(() => {
    return subscribeActivityFeed(setFeedItems);
  }, []);

  /*
    {
      id: 'feed_1',
      authorId: 'ath_marcus',
      authorName: 'Marcus Vance',
      authorAvatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=300',
      authorSport: 'basketball',
      authorLevel: 12,
      authorLevelTitle: 'Playground Hero',
      type: 'game_played',
      timestamp: '15 minutes ago',
      title: 'Logged 32 PTS, 8 AST, 5 REB at Rucker Park 🏀',
      description: 'Clutch 4th quarter shooting run! Won the pickup game 21-18 against Dyckman Select.',
      isFriend: true,
      courtName: 'Rucker Park Court #1',
      gameStatsSummary: '32 PTS • 8 AST • 5 REB • 60% FG',
      xpEarned: 250,
      likesCount: 18,
      hasLiked: false,
      comments: [
        {
          id: 'c1',
          authorName: 'Elena Rostova',
          authorAvatar: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?auto=format&fit=crop&q=80&w=300',
          text: 'Bucket getter! That stepback in the 4th was cold 🔥',
          timestamp: '10m ago',
        },
      ],
    },
    {
      id: 'feed_2',
      authorId: 'ath_elena',
      authorName: 'Elena Rostova',
      authorAvatar: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?auto=format&fit=crop&q=80&w=300',
      authorSport: 'basketball',
      authorLevel: 15,
      authorLevelTitle: 'League MVP',
      type: 'achievement_unlocked',
      timestamp: '1 hour ago',
      title: 'Unlocked Badge: "Triple-Double King" 🏆',
      description: 'Logged a game with 10+ PTS, 10+ REB, and 10+ AST in official playground tracking!',
      isFriend: true,
      badgeName: 'Triple-Double King',
      badgeTier: 'gold',
      xpEarned: 350,
      likesCount: 29,
      hasLiked: true,
      comments: [
        {
          id: 'c2',
          authorName: 'Darius Miller',
          authorAvatar: 'https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?auto=format&fit=crop&q=80&w=300',
          text: 'Legitimate MVP numbers! 👏',
          timestamp: '45m ago',
        },
      ],
    },
    {
      id: 'feed_3',
      authorId: 'ath_darius',
      authorName: 'Darius Miller',
      authorAvatar: 'https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?auto=format&fit=crop&q=80&w=300',
      authorSport: 'baseball',
      authorLevel: 9,
      authorLevelTitle: 'Rising Star',
      type: 'highlight_posted',
      timestamp: '3 hours ago',
      title: 'Uploaded a 30s Reel: "9th Inning Walkoff Home Run" ⚾',
      description: 'Check out this 30s highlight clip hitting a 410ft blast over the left field fence!',
      isFriend: true,
      highlightThumbnailUrl: 'https://images.unsplash.com/photo-1508344928928-7165b67de128?auto=format&fit=crop&q=80&w=600',
      videoDuration: '00:24',
      xpEarned: 150,
      likesCount: 34,
      hasLiked: false,
      comments: [],
    },
    {
      id: 'feed_4',
      authorId: 'ath_jordan',
      authorName: 'Jordan Rivera',
      authorAvatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=300',
      authorSport: 'soccer',
      authorLevel: 10,
      authorLevelTitle: 'Playground Hero',
      type: 'level_up',
      timestamp: '5 hours ago',
      title: 'LEVELED UP to Level 10 (Playground Hero)! ⚡',
      description: 'Passed 10,000 total career XP after winning the local 7v7 soccer tournament.',
      isFriend: false,
      xpEarned: 500,
      likesCount: 42,
      hasLiked: true,
      comments: [
        {
          id: 'c3',
          authorName: 'Sarah Chen',
          authorAvatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&q=80&w=300',
          text: 'Welcome to Level 10! 🔥',
          timestamp: '2h ago',
        },
      ],
    },
    {
      id: 'feed_5',
      authorId: 'ath_sarah',
      authorName: 'Sarah Chen',
      authorAvatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&q=80&w=300',
      authorSport: 'pickleball',
      authorLevel: 7,
      authorLevelTitle: 'Rising Star',
      type: 'court_checkin',
      timestamp: '7 hours ago',
      title: 'Checked in at Venice Beach Pickleball Courts 🏓',
      description: 'Hosting a 2v2 doubles open session. Come through and grab a paddle!',
      isFriend: true,
      courtName: 'Venice Beach Courts #3',
      likesCount: 14,
      hasLiked: false,
      comments: [],
    },
  ]); */

  // Handle Like/Hype toggle
  const handleToggleLike = (feedId: string) => {
    setFeedItems((prev) =>
      prev.map((item) => {
        if (item.id === feedId) {
          const newLiked = !item.hasLiked;
          return {
            ...item,
            hasLiked: newLiked,
            likesCount: newLiked ? item.likesCount + 1 : item.likesCount - 1,
          };
        }
        return item;
      })
    );
  };

  // Handle Posting Status Update
  const handlePostStatus = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newStatusText.trim()) return;

    const newPost: ActivityFeedItem = {
      id: `status_${Date.now()}`,
      authorId: user?.id || 'guest',
      authorName: user?.name || 'Athlete',
      authorAvatar: user?.avatar || '',
      authorSport: user?.primarySport || 'basketball',
      authorLevel: user?.level || 1,
      authorLevelTitle: user?.levelTitle || 'Rookie',
      type: 'status_update',
      timestamp: 'Just now',
      createdAt: Date.now(),
      title: 'Shared an Athlete Update 📢',
      description: newStatusText.trim(),
      isFriend: true,
      likesCount: 1,
      hasLiked: true,
      comments: [],
    };

    setFeedItems((prev) => [newPost, ...prev.filter((item) => item.id !== newPost.id)]);
    saveActivityFeedItemToFirestore(newPost);
    setNewStatusText('');
  };

  // Handle Adding Comment
  const handleAddComment = (feedId: string) => {
    const text = commentInputText[feedId]?.trim();
    if (!text) return;

    const newComment: FeedComment = {
      id: `comment_${Date.now()}`,
      authorName: user?.name || 'Athlete',
      authorAvatar: user?.avatar || '',
      text: text,
      timestamp: 'Just now',
    };

    setFeedItems((prev) =>
      prev.map((item) => {
        if (item.id === feedId) {
          return {
            ...item,
            comments: [...item.comments, newComment],
          };
        }
        return item;
      })
    );

    setCommentInputText((prev) => ({ ...prev, [feedId]: '' }));
  };

  const handleSendFriendRequestTrigger = (player: AthleteProfile) => {
    if (sentFriendRequests.includes(player.id)) return;
    setSentFriendRequests((prev) => [...prev, player.id]);
    onSendFriendRequest?.({
      id: player.id,
      name: player.name,
      avatar: player.avatar,
      detail: `${player.levelTitle} • Level ${player.level}`,
    });
  };

  // Filter feed items
  const filteredFeed = feedItems.filter((item) => {
    if (friendOnlyFilter && !item.isFriend && item.authorId !== user.id) return false;
    if (filterType === 'game' && item.type !== 'game_played') return false;
    if (filterType === 'achievement' && item.type !== 'achievement_unlocked') return false;
    if (filterType === 'highlight' && item.type !== 'highlight_posted') return false;
    if (filterType === 'levelup' && item.type !== 'level_up') return false;
    return true;
  });

  return (
    <div className="space-y-6 pb-20 lg:pb-8 animate-fadeIn text-white">
      {/* Top Banner Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 bg-indigo-900/60 p-6 rounded-[2.5rem] border border-white/10 shadow-xl">
        <div className="space-y-1">
          <div className="flex items-center space-x-2">
            <Rss className="w-6 h-6 text-lime-400 stroke-[2.5]" />
            <h1 className="text-2xl font-black italic uppercase tracking-tight text-white">
              Athlete Social Feed & Activity Stream
            </h1>
          </div>
          <p className="text-xs text-indigo-200/70 font-semibold max-w-2xl">
            See real-time game results, verified stat logs, unlocked achievements, and 30s highlight clips from your athlete network.
          </p>
        </div>

        {/* Quick Tabs to Navigation */}
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setShowPlayerCompare(!showPlayerCompare)}
            className={`px-4 py-2.5 rounded-2xl font-black italic uppercase text-xs transition flex items-center space-x-1.5 shadow-lg border ${
              showPlayerCompare
                ? 'bg-lime-400 text-black border-lime-300 ring-2 ring-lime-400/50'
                : 'bg-indigo-950 hover:bg-indigo-800 text-lime-400 border-lime-400/40'
            }`}
          >
            <Swords className="w-4 h-4 stroke-[2.5]" />
            <span>{showPlayerCompare ? 'Hide Radar Compare' : 'Player Compare (D3)'}</span>
          </button>

          <button
            onClick={() => onNavigateTab('games')}
            className="px-4 py-2.5 bg-indigo-950 hover:bg-indigo-800 text-indigo-200 border border-white/10 font-black italic uppercase text-xs rounded-2xl transition flex items-center space-x-1.5"
          >
            <CalendarCheck className="w-4 h-4 text-lime-400" />
            <span>Pickup Games</span>
          </button>

          <button
            onClick={() => onNavigateTab('highlights')}
            className="px-4 py-2.5 bg-lime-400 hover:bg-lime-300 text-black font-black italic uppercase text-xs rounded-2xl shadow-lg shadow-lime-400/20 transition flex items-center space-x-1.5"
          >
            <Film className="w-4 h-4 stroke-[2.5]" />
            <span>Highlights & Reels</span>
          </button>
        </div>
      </div>

      {/* SIDE-BY-SIDE PLAYER COMPARE RADAR CHART */}
      {showPlayerCompare && (
        <PlayerCompareRadarChart
          player1={comparePlayer1}
          player2={comparePlayer2}
          allAthletes={allAthletes}
          onSelectPlayer1={setComparePlayer1}
          onSelectPlayer2={setComparePlayer2}
          onClose={() => setShowPlayerCompare(false)}
        />
      )}

      {/* CREATE STATUS POST BOX */}
      <div className="bg-indigo-900/60 p-5 rounded-[2.5rem] border border-white/10 shadow-xl space-y-3">
        <div className="flex items-center space-x-3">
          <img
            src={user?.avatar || ''}
            alt={user?.name || 'Athlete'}
            className="w-10 h-10 rounded-2xl object-cover ring-2 ring-lime-400/50"
          />
          <div className="flex-1">
            <p className="text-xs font-black italic text-white">{user?.name || 'Athlete'}</p>
            <p className="text-[10px] text-lime-300 font-extrabold uppercase">{user?.levelTitle || 'Rookie'} • Lvl {user?.level || 1}</p>
          </div>
        </div>

        <form onSubmit={handlePostStatus} className="space-y-3">
          <textarea
            value={newStatusText}
            onChange={(e) => setNewStatusText(e.target.value)}
            placeholder="Share a game update, workout milestone, or challenge completed with your athlete network..."
            rows={2}
            className="w-full bg-indigo-950/80 text-white placeholder-indigo-300/50 text-xs rounded-2xl p-3 border border-white/10 focus:border-lime-400 focus:outline-none transition resize-none font-medium"
          />

          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 text-[10px] text-indigo-300/70 font-semibold">
              <Sparkles className="w-3.5 h-3.5 text-lime-400" />
              <span>Visible to all connected athletes and playground courts</span>
            </div>

            <button
              type="submit"
              disabled={!newStatusText.trim()}
              className={`px-5 py-2.5 rounded-xl font-black italic uppercase text-xs flex items-center space-x-1.5 transition ${
                newStatusText.trim()
                  ? 'bg-lime-400 hover:bg-lime-300 text-black shadow-lg shadow-lime-400/20'
                  : 'bg-indigo-950 text-indigo-400 border border-white/5 cursor-not-allowed'
              }`}
            >
              <Send className="w-3.5 h-3.5" />
              <span>Share Update</span>
            </button>
          </div>
        </form>
      </div>

      {/* MAIN CONTENT GRID */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* FEED STREAM COLUMN */}
        <div className="lg:col-span-8 space-y-4">
          {/* FILTER BAR */}
          <div className="bg-indigo-900/60 p-4 rounded-3xl border border-white/10 flex flex-wrap items-center justify-between gap-3 text-xs font-bold">
            <div className="flex items-center space-x-1.5 overflow-x-auto pb-1 max-w-full">
              <button
                onClick={() => setFilterType('all')}
                className={`px-3 py-1.5 rounded-xl transition shrink-0 ${
                  filterType === 'all'
                    ? 'bg-lime-400 text-black font-black italic'
                    : 'bg-indigo-950 text-indigo-200 border border-white/10 hover:text-white'
                }`}
              >
                All Updates 🌐
              </button>
              <button
                onClick={() => setFilterType('game')}
                className={`px-3 py-1.5 rounded-xl transition shrink-0 ${
                  filterType === 'game'
                    ? 'bg-lime-400 text-black font-black italic'
                    : 'bg-indigo-950 text-indigo-200 border border-white/10 hover:text-white'
                }`}
              >
                Games & Stats 🏀
              </button>
              <button
                onClick={() => setFilterType('achievement')}
                className={`px-3 py-1.5 rounded-xl transition shrink-0 ${
                  filterType === 'achievement'
                    ? 'bg-lime-400 text-black font-black italic'
                    : 'bg-indigo-950 text-indigo-200 border border-white/10 hover:text-white'
                }`}
              >
                Achievements 🏆
              </button>
              <button
                onClick={() => setFilterType('highlight')}
                className={`px-3 py-1.5 rounded-xl transition shrink-0 ${
                  filterType === 'highlight'
                    ? 'bg-lime-400 text-black font-black italic'
                    : 'bg-indigo-950 text-indigo-200 border border-white/10 hover:text-white'
                }`}
              >
                Highlights 📹
              </button>
              <button
                onClick={() => setFilterType('levelup')}
                className={`px-3 py-1.5 rounded-xl transition shrink-0 ${
                  filterType === 'levelup'
                    ? 'bg-lime-400 text-black font-black italic'
                    : 'bg-indigo-950 text-indigo-200 border border-white/10 hover:text-white'
                }`}
              >
                Level Ups ⚡
              </button>
            </div>

            {/* Friends-Only Visibility Toggle */}
            <div className="flex items-center space-x-2 shrink-0">
              <span className="text-[10px] font-mono uppercase text-indigo-300 font-bold hidden sm:inline">
                Feed Visibility:
              </span>
              <button
                type="button"
                onClick={() => setFriendOnlyFilter(!friendOnlyFilter)}
                className={`px-3.5 py-1.5 rounded-xl border text-[11px] font-black italic uppercase transition flex items-center space-x-1.5 shadow-md ${
                  friendOnlyFilter
                    ? 'bg-lime-400 text-black border-lime-300 ring-2 ring-lime-400/40'
                    : 'bg-indigo-950 text-indigo-200 border-white/10 hover:text-white hover:border-lime-400/30'
                }`}
                title={friendOnlyFilter ? 'Showing posts exclusively from your friends list' : 'Showing posts from all global athletes'}
              >
                <Users className="w-3.5 h-3.5 stroke-[2.5]" />
                <span>{friendOnlyFilter ? 'Friends Only 👥' : 'All Athletes 🌐'}</span>
              </button>
            </div>
          </div>

          {/* FRIENDS-ONLY FILTER ACTIVE INDICATOR BANNER */}
          {friendOnlyFilter && (
            <div className="p-3 bg-indigo-950/90 border border-lime-400/40 rounded-2xl flex items-center justify-between text-xs text-lime-300 animate-fadeIn">
              <div className="flex items-center space-x-2">
                <Users className="w-4 h-4 text-lime-400 shrink-0" />
                <span>
                  <strong>Friends-Only View Active:</strong> Filtering activity stream to show posts exclusively from your connected athlete friends.
                </span>
              </div>
              <button
                type="button"
                onClick={() => setFriendOnlyFilter(false)}
                className="text-[10px] font-mono font-bold uppercase text-indigo-300 hover:text-white underline shrink-0 ml-2"
              >
                Show All
              </button>
            </div>
          )}

          {/* ACTIVITY FEED CARDS LIST */}
          {filteredFeed.length === 0 ? (
            <div className="p-10 text-center bg-indigo-900/50 rounded-[2.5rem] border border-white/10 shadow-xl space-y-4 relative overflow-hidden">
              <div className="w-20 h-20 mx-auto rounded-3xl bg-indigo-950/80 border-2 border-lime-400/40 flex items-center justify-center text-lime-400 shadow-[0_0_30px_rgba(163,230,53,0.15)]">
                <Rss className="w-10 h-10 animate-pulse" />
              </div>

              <div className="space-y-1.5 max-w-md mx-auto">
                <h3 className="text-lg font-black italic uppercase text-white tracking-tight">
                  No Community Activity Updates
                </h3>
                <p className="text-xs text-indigo-200/70 leading-relaxed">
                  {friendOnlyFilter
                    ? "None of your connected friends have posted recent game logs or status updates yet."
                    : filterType !== 'all'
                    ? `No feed activities found matching the "${filterType.replace('_', ' ')}" filter tag.`
                    : "The court feed is quiet right now. Be the first athlete to drop a status update, game result, or 30s highlight clip!"}
                </p>
              </div>

              <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
                {friendOnlyFilter || filterType !== 'all' ? (
                  <button
                    type="button"
                    onClick={() => {
                      setFriendOnlyFilter(false);
                      setFilterType('all');
                    }}
                    className="px-4 py-2.5 bg-indigo-950 hover:bg-indigo-800 text-lime-300 border border-lime-400/40 text-xs font-black italic uppercase rounded-xl transition flex items-center space-x-1.5"
                  >
                    <Filter className="w-3.5 h-3.5" />
                    <span>Clear Feed Filters</span>
                  </button>
                ) : null}

                <button
                  type="button"
                  onClick={() => {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="px-5 py-2.5 bg-lime-400 hover:bg-lime-300 text-black font-black italic uppercase text-xs rounded-xl shadow-md transition flex items-center space-x-1.5"
                >
                  <Send className="w-3.5 h-3.5" />
                  <span>Share Status Update</span>
                </button>
              </div>
            </div>
          ) : (
            filteredFeed.map((item) => (
              <div
                key={item.id}
                className="bg-indigo-900/60 p-6 rounded-[2.5rem] border border-white/10 shadow-xl space-y-4 relative overflow-hidden transition hover:border-white/20"
              >
                {/* Header: Author info */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <img
                      src={item.authorAvatar}
                      alt={item.authorName}
                      className="w-12 h-12 rounded-2xl object-cover ring-2 ring-lime-400/30"
                    />
                    <div>
                      <div className="flex items-center space-x-2">
                        <span className="font-black italic text-sm text-white flex items-center gap-1">
                          <span>{item.authorName}</span>
                          {(item.authorIsPro || (user?.name === item.authorName && user?.isPro) || allAthletes.find(a => a.id === item.authorId || a.name === item.authorName)?.isPro) && (
                            <span className="bg-amber-400 text-black font-black text-[9px] px-1.5 py-0.2 rounded font-mono flex items-center gap-0.5 shrink-0" title="PRO Member">
                              <Crown className="w-2.5 h-2.5 fill-black" />
                              <span>PRO</span>
                            </span>
                          )}
                        </span>
                        <span className="px-2 py-0.5 bg-indigo-950 text-lime-300 text-[10px] font-black uppercase rounded-full border border-lime-400/20">
                          Lvl {item.authorLevel} • {item.authorLevelTitle}
                        </span>
                      </div>
                      <p className="text-[10px] text-indigo-300/70 font-semibold flex items-center mt-0.5">
                        <Clock className="w-3 h-3 mr-1 text-indigo-400" />
                        {item.timestamp} • <span className="uppercase text-lime-400 font-bold ml-1">{item.authorSport}</span>
                      </p>
                    </div>
                  </div>

                  {/* Badge Pill for Activity Type */}
                  <div className="shrink-0">
                    {item.type === 'game_played' && (
                      <span className="px-3 py-1 bg-amber-500/20 text-amber-300 border border-amber-500/40 text-[10px] font-black uppercase rounded-full flex items-center">
                        <Trophy className="w-3 h-3 mr-1" /> Game Logged
                      </span>
                    )}
                    {item.type === 'achievement_unlocked' && (
                      <span className="px-3 py-1 bg-lime-400/20 text-lime-300 border border-lime-400/40 text-[10px] font-black uppercase rounded-full flex items-center">
                        <Award className="w-3 h-3 mr-1" /> Achievement
                      </span>
                    )}
                    {item.type === 'highlight_posted' && (
                      <span className="px-3 py-1 bg-indigo-500/20 text-indigo-300 border border-indigo-500/40 text-[10px] font-black uppercase rounded-full flex items-center">
                        <Film className="w-3 h-3 mr-1" /> 30s Reel
                      </span>
                    )}
                    {item.type === 'level_up' && (
                      <span className="px-3 py-1 bg-purple-500/20 text-purple-300 border border-purple-500/40 text-[10px] font-black uppercase rounded-full flex items-center">
                        <Zap className="w-3 h-3 mr-1 fill-purple-300" /> Level Up
                      </span>
                    )}
                    {item.type === 'court_checkin' && (
                      <span className="px-3 py-1 bg-rose-500/20 text-rose-300 border border-rose-500/40 text-[10px] font-black uppercase rounded-full flex items-center">
                        <MapPin className="w-3 h-3 mr-1" /> Check-In
                      </span>
                    )}
                    {item.type === 'status_update' && (
                      <span className="px-3 py-1 bg-indigo-950 text-indigo-300 border border-white/10 text-[10px] font-black uppercase rounded-full flex items-center">
                        <Sparkles className="w-3 h-3 mr-1 text-lime-400" /> Status
                      </span>
                    )}
                  </div>
                </div>

                {/* Body Content */}
                <div className="space-y-2 bg-indigo-950/60 p-4 rounded-2xl border border-white/5">
                  <h3 className="font-extrabold text-sm text-white">{item.title}</h3>
                  <p className="text-xs text-indigo-200/80 font-medium leading-relaxed">{item.description}</p>

                  {/* Highlights Media Box if type === 'highlight_posted' */}
                  {item.type === 'highlight_posted' && item.highlightThumbnailUrl && (
                    <div className="mt-3 relative rounded-2xl overflow-hidden aspect-video bg-black border border-white/10 group">
                      <img
                        src={item.highlightThumbnailUrl}
                        alt="Highlight reel"
                        className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                        referrerPolicy="no-referrer"
                      />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <button
                          onClick={() => onNavigateTab('highlights')}
                          className="p-3 bg-lime-400 text-black rounded-full shadow-lg hover:scale-110 transition"
                        >
                          <Film className="w-6 h-6 stroke-[2.5]" />
                        </button>
                      </div>
                      {item.videoDuration && (
                        <span className="absolute bottom-2 right-2 px-2 py-0.5 bg-black/80 text-[10px] font-mono text-white rounded">
                          {item.videoDuration}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Stats pill if game_played */}
                  {item.type === 'game_played' && item.gameStatsSummary && (
                    <div className="mt-2 p-2.5 bg-indigo-900/60 rounded-xl border border-lime-400/30 flex items-center justify-between text-xs font-black">
                      <span className="text-lime-300 font-mono">{item.gameStatsSummary}</span>
                      <span className="text-[10px] text-indigo-200/70">{item.courtName}</span>
                    </div>
                  )}
                </div>

                {/* Footer Interactions (Hype/Like, Comment, Share) */}
                <div className="space-y-3 pt-1">
                  <div className="flex items-center justify-between text-xs font-bold text-indigo-300/80 border-t border-white/10 pt-3">
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => handleToggleLike(item.id)}
                        className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-xl border transition ${
                          item.hasLiked
                            ? 'bg-rose-500/20 text-rose-300 border-rose-500/40 font-black'
                            : 'bg-indigo-950 text-indigo-200 border-white/10 hover:text-white'
                        }`}
                      >
                        <Flame className={`w-4 h-4 ${item.hasLiked ? 'text-rose-400 fill-rose-400' : ''}`} />
                        <span>{item.likesCount} Hypes</span>
                      </button>

                      <button
                        onClick={() =>
                          setActiveCommentFeedId(activeCommentFeedId === item.id ? null : item.id)
                        }
                        className="flex items-center space-x-1.5 px-3 py-1.5 bg-indigo-950 hover:bg-indigo-800 text-indigo-200 border border-white/10 rounded-xl transition"
                      >
                        <MessageCircle className="w-4 h-4" />
                        <span>{item.comments.length} Comments</span>
                      </button>
                    </div>

                    <button
                      onClick={() => alert('Activity link copied to clipboard!')}
                      className="p-1.5 bg-indigo-950 hover:bg-indigo-800 text-indigo-300 rounded-xl border border-white/10 transition"
                      title="Share"
                    >
                      <Share2 className="w-4 h-4" />
                    </button>
                  </div>

                  {/* COMMENTS DRAWER */}
                  {activeCommentFeedId === item.id && (
                    <div className="bg-indigo-950/80 p-4 rounded-2xl border border-white/10 space-y-3 animate-fadeIn">
                      <div className="space-y-2 max-h-40 overflow-y-auto pr-1">
                        {item.comments.length === 0 ? (
                          <p className="text-[11px] text-indigo-300/60 italic">No comments yet. Be the first to cheer!</p>
                        ) : (
                          item.comments.map((c) => (
                            <div key={c.id} className="flex items-start space-x-2 text-xs">
                              <div className="w-6 h-6 rounded-full bg-lime-400 text-black font-black flex items-center justify-center shrink-0 text-[10px]">
                                {c.authorName[0]}
                              </div>
                              <div className="bg-indigo-900/60 p-2 rounded-xl flex-1 border border-white/5 space-y-0.5">
                                <div className="flex justify-between items-center text-[10px]">
                                  <span className="font-extrabold text-lime-300">{c.authorName}</span>
                                  <span className="text-indigo-400">{c.timestamp}</span>
                                </div>
                                <p className="text-indigo-100 font-medium">{c.text}</p>
                              </div>
                            </div>
                          ))
                        )}
                      </div>

                      {/* Add Comment Input */}
                      <div className="flex items-center space-x-2 pt-1">
                        <input
                          type="text"
                          value={commentInputText[item.id] || ''}
                          onChange={(e) =>
                            setCommentInputText({ ...commentInputText, [item.id]: e.target.value })
                          }
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleAddComment(item.id);
                          }}
                          placeholder="Write a cheer or comment..."
                          className="flex-1 bg-indigo-900 text-white placeholder-indigo-300/50 text-xs px-3 py-2 rounded-xl border border-white/10 focus:border-lime-400 focus:outline-none"
                        />
                        <button
                          type="button"
                          onClick={() => handleAddComment(item.id)}
                          className="px-3 py-2 bg-lime-400 hover:bg-lime-300 text-black font-black italic uppercase text-xs rounded-xl transition"
                        >
                          Post
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* SIDEBAR WIDGETS COLUMN */}
        <div className="lg:col-span-4 space-y-6">
          {/* ATHLETE DIRECTORY & CONNECT SEARCH */}
          <div className="bg-indigo-900/60 p-5 rounded-[2.5rem] border border-white/10 shadow-xl space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-black italic uppercase text-sm text-white flex items-center">
                  <UserPlus className="w-4 h-4 text-lime-400 mr-2" />
                  Athlete Directory
                </h3>
                <p className="text-[10px] text-indigo-200/70 font-medium">Find local PROs, match skill levels & connect</p>
              </div>
              <div className="flex items-center space-x-1.5">
                {activeAdvancedFilterCount > 0 && (
                  <span className="text-[10px] text-amber-300 font-mono font-black bg-amber-500/20 px-2 py-0.5 rounded-full border border-amber-500/40 animate-pulse">
                    {activeAdvancedFilterCount} ACTIVE
                  </span>
                )}
                <span className="text-[10px] text-lime-300 font-mono font-bold bg-indigo-950 px-2.5 py-0.5 rounded-full border border-white/10">
                  {filteredAthletes.length} Found
                </span>
              </div>
            </div>

            {/* Search Input */}
            <div className="space-y-2.5">
              <div className="relative">
                <Search className="w-4 h-4 text-indigo-300/60 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={athleteSearchQuery}
                  onChange={(e) => setAthleteSearchQuery(e.target.value)}
                  placeholder="Search athletes by name, school/league, or primary sport..."
                  className="w-full bg-indigo-950 text-white placeholder-indigo-300/40 text-xs pl-9 pr-8 py-2.5 rounded-xl border border-white/10 focus:border-lime-400 focus:outline-none transition font-medium shadow-inner"
                />
                {athleteSearchQuery && (
                  <button
                    type="button"
                    onClick={() => setAthleteSearchQuery('')}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-indigo-300/60 hover:text-white p-0.5"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              {/* QUICK ADVANCED FILTER TOGGLE PILLS */}
              <div className="flex items-center gap-1.5 flex-wrap text-[10px]">
                {/* PRO Only Toggle */}
                <button
                  type="button"
                  onClick={() => setProOnlyFilter(!proOnlyFilter)}
                  className={`px-2.5 py-1 rounded-xl font-black italic uppercase transition flex items-center space-x-1 border ${
                    proOnlyFilter
                      ? 'bg-amber-400 text-black border-amber-300 shadow-lg shadow-amber-400/20'
                      : 'bg-indigo-950/80 text-amber-300/90 border-amber-400/30 hover:border-amber-400'
                  }`}
                >
                  <Crown className={`w-3 h-3 ${proOnlyFilter ? 'fill-black' : 'fill-amber-400'}`} />
                  <span>PRO Members</span>
                </button>

                {/* Nearby Toggle */}
                <button
                  type="button"
                  onClick={() => {
                    const next = !nearbyOnlyFilter;
                    setNearbyOnlyFilter(next);
                    if (next && maxDistanceKm === 'all') setMaxDistanceKm('25');
                  }}
                  className={`px-2.5 py-1 rounded-xl font-black italic uppercase transition flex items-center space-x-1 border ${
                    nearbyOnlyFilter || maxDistanceKm !== 'all'
                      ? 'bg-sky-400 text-black border-sky-300 shadow-lg shadow-sky-400/20'
                      : 'bg-indigo-950/80 text-sky-300/90 border-sky-400/30 hover:border-sky-400'
                  }`}
                >
                  <MapPin className="w-3 h-3 text-current" />
                  <span>Nearby (&lt;25km)</span>
                </button>

                {/* Similar Skill Toggle */}
                <button
                  type="button"
                  onClick={() => setSimilarSkillFilter(!similarSkillFilter)}
                  className={`px-2.5 py-1 rounded-xl font-black italic uppercase transition flex items-center space-x-1 border ${
                    similarSkillFilter
                      ? 'bg-lime-400 text-black border-lime-300 shadow-lg shadow-lime-400/20'
                      : 'bg-indigo-950/80 text-lime-300/90 border-lime-400/30 hover:border-lime-400'
                  }`}
                >
                  <Target className="w-3 h-3 text-current" />
                  <span>Similar Skill (Lvl {user.level - 3}-{user.level + 3})</span>
                </button>

                {/* My Sport Toggle */}
                <button
                  type="button"
                  onClick={() => setSameSportFilter(!sameSportFilter)}
                  className={`px-2.5 py-1 rounded-xl font-black italic uppercase transition flex items-center space-x-1 border ${
                    sameSportFilter
                      ? 'bg-emerald-400 text-black border-emerald-300 shadow-lg shadow-emerald-400/20'
                      : 'bg-indigo-950/80 text-emerald-300/90 border-emerald-400/30 hover:border-emerald-400'
                  }`}
                >
                  <span>{user.primarySport === 'basketball' ? '🏀' : user.primarySport === 'pickleball' ? '🏓' : user.primarySport === 'soccer' ? '⚽' : '⚾'}</span>
                  <span>My Sport ({user.primarySport})</span>
                </button>

                {/* Expand More Filters Toggle Button */}
                <button
                  type="button"
                  onClick={() => setShowAdvancedPanel(!showAdvancedPanel)}
                  className={`px-2 py-1 rounded-xl font-bold text-[10px] transition flex items-center space-x-1 border ml-auto ${
                    showAdvancedPanel
                      ? 'bg-indigo-800 text-white border-white/30'
                      : 'bg-indigo-950/80 text-indigo-300 border-white/10 hover:text-white'
                  }`}
                >
                  <SlidersHorizontal className="w-3 h-3" />
                  <span>{showAdvancedPanel ? 'Less' : 'More'}</span>
                </button>
              </div>

              {/* EXPANDABLE DETAILED FILTERS PANEL */}
              {showAdvancedPanel && (
                <div className="bg-indigo-950/90 p-3 rounded-2xl border border-white/10 space-y-2.5 animate-fadeIn text-xs">
                  <div className="grid grid-cols-3 gap-2">
                    {/* Distance Select */}
                    <div>
                      <label className="text-[9px] font-black uppercase text-indigo-300/70 block mb-0.5">Max Distance</label>
                      <select
                        value={maxDistanceKm}
                        onChange={(e) => {
                          setMaxDistanceKm(e.target.value);
                          if (e.target.value !== 'all') setNearbyOnlyFilter(true);
                        }}
                        className="w-full bg-indigo-900 text-white text-[11px] font-bold p-1.5 rounded-lg border border-white/10 focus:border-lime-400 focus:outline-none"
                      >
                        <option value="all">Any Distance</option>
                        <option value="5">Within 5 km</option>
                        <option value="15">Within 15 km</option>
                        <option value="25">Within 25 km</option>
                        <option value="50">Within 50 km</option>
                      </select>
                    </div>

                    {/* Sport Dropdown */}
                    <div>
                      <label className="text-[9px] font-black uppercase text-indigo-300/70 block mb-0.5">Sport</label>
                      <select
                        value={athleteSportFilter}
                        onChange={(e) => setAthleteSportFilter(e.target.value)}
                        className="w-full bg-indigo-900 text-white text-[11px] font-bold p-1.5 rounded-lg border border-white/10 focus:border-lime-400 focus:outline-none"
                      >
                        <option value="all">All Sports</option>
                        <option value="basketball">Basketball 🏀</option>
                        <option value="pickleball">Pickleball 🏓</option>
                        <option value="soccer">Soccer ⚽</option>
                        <option value="baseball">Baseball ⚾</option>
                        <option value="softball">Softball 🥎</option>
                        <option value="volleyball">Volleyball 🏐</option>
                        <option value="football">Football 🏈</option>
                        <option value="tennis">Tennis 🎾</option>
                      </select>
                    </div>

                    {/* Skill Title Dropdown */}
                    <div>
                      <label className="text-[9px] font-black uppercase text-indigo-300/70 block mb-0.5">Skill Level</label>
                      <select
                        value={athleteSkillFilter}
                        onChange={(e) => setAthleteSkillFilter(e.target.value)}
                        className="w-full bg-indigo-900 text-white text-[11px] font-bold p-1.5 rounded-lg border border-white/10 focus:border-lime-400 focus:outline-none"
                      >
                        <option value="all">All Titles</option>
                        <option value="Rookie">Rookie</option>
                        <option value="Rising Star">Rising Star</option>
                        <option value="Playground Hero">Playground Hero</option>
                        <option value="League MVP">League MVP</option>
                        <option value="Hall of Famer">Hall of Famer</option>
                      </select>
                    </div>
                  </div>

                  {/* Reset All Filters Link */}
                  {activeAdvancedFilterCount > 0 && (
                    <div className="flex justify-end pt-1">
                      <button
                        type="button"
                        onClick={handleResetFilters}
                        className="text-[10px] text-rose-300 hover:text-rose-200 underline font-bold flex items-center gap-1"
                      >
                        <X className="w-3 h-3" />
                        <span>Reset All Directory Filters</span>
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Filtered Athletes List */}
            <div className="space-y-2.5 max-h-96 overflow-y-auto pr-1">
              {filteredAthletes.length === 0 ? (
                <div className="p-6 bg-indigo-950/80 rounded-2xl border border-white/10 text-center space-y-3">
                  <div className="w-12 h-12 mx-auto rounded-2xl bg-indigo-900/60 border border-white/10 flex items-center justify-center text-indigo-300">
                    <UserPlus className="w-6 h-6 text-indigo-400" />
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs font-black italic uppercase text-white">No Athletes Found</p>
                    <p className="text-[11px] text-indigo-200/70 leading-normal">
                      No registered athletes match your current PRO, distance, sport, or skill level search criteria.
                    </p>
                  </div>
                  {activeAdvancedFilterCount > 0 && (
                    <button
                      type="button"
                      onClick={handleResetFilters}
                      className="px-3.5 py-1.5 bg-lime-400 hover:bg-lime-300 text-black font-black italic uppercase text-[10px] rounded-xl transition inline-flex items-center space-x-1 shadow"
                    >
                      <X className="w-3 h-3" />
                      <span>Reset Filters & Show All ({allAthletes.length})</span>
                    </button>
                  )}
                </div>
              ) : (
                filteredAthletes.map((ath) => {
                  const isSent = sentFriendRequests.includes(ath.id);
                  const isPro = Boolean(ath.isPro || ath.subscriptionTier === 'pro');
                  const distanceKm = getDistanceKm(
                    user.location?.lat,
                    user.location?.lng,
                    ath.location?.lat,
                    ath.location?.lng
                  );

                  return (
                    <div
                      key={ath.id}
                      className={`p-3.5 rounded-2xl transition flex items-center justify-between space-x-2 relative ${
                        isPro
                          ? 'bg-indigo-950/95 border-2 border-amber-400/60 shadow-lg shadow-amber-500/10 hover:border-amber-400'
                          : 'bg-indigo-950/80 border border-white/5 hover:border-white/20'
                      }`}
                    >
                      <div className="flex items-center space-x-2.5 min-w-0">
                        <div className="relative shrink-0">
                          <img
                            src={ath.avatar}
                            alt={ath.name}
                            className={`w-10 h-10 rounded-xl object-cover ${
                              isPro ? 'ring-2 ring-amber-400' : 'ring-1 ring-lime-400/40'
                            }`}
                          />
                          <span className="absolute -bottom-1 -right-1 text-[9px] bg-indigo-950 p-0.5 rounded-full border border-white/10">
                            {ath.primarySport === 'basketball'
                              ? '🏀'
                              : ath.primarySport === 'pickleball'
                              ? '🏓'
                              : ath.primarySport === 'soccer'
                              ? '⚽'
                              : ath.primarySport === 'baseball'
                              ? '⚾'
                              : '🎾'}
                          </span>
                        </div>
                        <div className="min-w-0 space-y-0.5">
                          <div className="flex items-center space-x-1.5">
                            <p className="text-xs font-bold text-white truncate flex items-center gap-1">
                              <span className="truncate">{ath.name}</span>
                              {isPro && (
                                <span
                                  className="bg-amber-400 text-black font-black text-[9px] px-1.5 py-0.2 rounded font-mono flex items-center gap-0.5 shrink-0 shadow"
                                  title="Verified PRO Member"
                                >
                                  <Crown className="w-2.5 h-2.5 fill-black" />
                                  <span>PRO</span>
                                </span>
                              )}
                            </p>
                            <span className="px-1.5 py-0.2 bg-lime-400/20 text-lime-300 text-[9px] font-black rounded font-mono shrink-0">
                              Lvl {ath.level}
                            </span>
                          </div>
                          
                          <div className="flex flex-wrap items-center gap-1.5 text-[10px] text-indigo-300/80">
                            <span className="font-extrabold text-lime-400 capitalize">{ath.primarySport}</span>
                            {ath.schoolOrLeague && (
                              <>
                                <span>•</span>
                                <span className="text-white font-medium truncate max-w-[120px]">{ath.schoolOrLeague}</span>
                              </>
                            )}
                            <span>•</span>
                            <span className="text-sky-300 font-medium shrink-0 flex items-center gap-0.5">
                              <MapPin className="w-2.5 h-2.5 text-sky-400 inline" />
                              {distanceKm} km
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center space-x-1.5 shrink-0">
                        {/* Direct Message Button */}
                        <button
                          type="button"
                          onClick={() => {
                            if (onDirectMessageAthlete) {
                              onDirectMessageAthlete(ath);
                            } else {
                              onNavigateTab('chat');
                            }
                          }}
                          className="px-2.5 py-1.5 rounded-xl text-[10px] font-black italic uppercase bg-indigo-900 hover:bg-lime-400 hover:text-black border border-lime-400/40 text-lime-300 transition flex items-center space-x-1 shadow"
                          title={`Direct Message ${ath.name}`}
                        >
                          <MessageSquare className="w-3 h-3 text-lime-400 group-hover:text-black" />
                          <span className="hidden sm:inline">Message</span>
                        </button>

                        <button
                          type="button"
                          onClick={() => {
                            setComparePlayer2(ath);
                            setShowPlayerCompare(true);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                          className="px-2.5 py-1.5 rounded-xl text-[10px] font-black italic uppercase bg-indigo-900 hover:bg-cyan-400 hover:text-black border border-cyan-400/40 text-cyan-300 transition flex items-center space-x-1 shadow"
                          title="Compare stats in Radar Chart"
                        >
                          <Swords className="w-3 h-3" />
                          <span className="hidden sm:inline">Compare</span>
                        </button>

                        <button
                          type="button"
                          onClick={() => handleSendFriendRequestTrigger(ath)}
                          disabled={isSent}
                          className={`px-3 py-1.5 rounded-xl text-[10px] font-black italic uppercase flex items-center space-x-1 transition ${
                            isSent
                              ? 'bg-indigo-900 text-lime-400 border border-lime-400/30'
                              : isPro
                              ? 'bg-amber-400 hover:bg-amber-300 text-black shadow-md shadow-amber-400/20'
                              : 'bg-lime-400 hover:bg-lime-300 text-black shadow-md'
                          }`}
                        >
                          {isSent ? <UserCheck className="w-3.5 h-3.5" /> : <UserPlus className="w-3.5 h-3.5" />}
                          <span>{isSent ? 'Sent' : 'Connect'}</span>
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* RECENT FRIEND MILESTONES SUMMARY */}
          <div className="bg-indigo-900/60 p-5 rounded-[2.5rem] border border-white/10 shadow-xl space-y-4">
            <h3 className="font-black italic uppercase text-sm text-white flex items-center">
              <TrendingUp className="w-4 h-4 text-lime-400 mr-2" />
              Community Milestones
            </h3>

            <div className="space-y-2 text-xs">
              <div className="p-3 bg-indigo-950/80 rounded-2xl border border-white/5 flex items-center justify-between">
                <div>
                  <p className="font-extrabold text-white">🔥 Most Hype Game</p>
                  <p className="text-[10px] text-indigo-300/70">Marcus Vance • 32 PTS at Rucker</p>
                </div>
                <span className="text-xs font-black text-lime-400 font-mono">+250 XP</span>
              </div>

              <div className="p-3 bg-indigo-950/80 rounded-2xl border border-white/5 flex items-center justify-between">
                <div>
                  <p className="font-extrabold text-white">🏆 Latest Badge Unlocked</p>
                  <p className="text-[10px] text-indigo-300/70">Elena Rostova • Triple-Double King</p>
                </div>
                <span className="text-xs font-black text-lime-400 font-mono">Gold Tier</span>
              </div>

              <div className="p-3 bg-indigo-950/80 rounded-2xl border border-white/5 flex items-center justify-between">
                <div>
                  <p className="font-extrabold text-white">⚡ Highest Level Up</p>
                  <p className="text-[10px] text-indigo-300/70">Jordan Rivera • Level 10</p>
                </div>
                <span className="text-xs font-black text-lime-400 font-mono">Lvl 10</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
